import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { Flag } from "../components/Flag";
import { fetchHistory } from "../lib/api";

export default function History() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory()
      .then((m) => {
        setMatches(m);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pb-16">
      <section className="border-b border-zinc-900 bg-zinc-950">
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">Archive</div>
          <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tighter sm:text-6xl">
            Match <span className="text-volt">History</span>
          </h1>
          <p className="mt-3 max-w-2xl font-body text-sm text-zinc-400">
            Every simulation you&apos;ve run, stored chronologically.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-10">
        {loading ? (
          <div className="text-zinc-400">Loading…</div>
        ) : matches.length === 0 ? (
          <div className="border border-zinc-900 bg-black p-10 text-center">
            <Clock className="mx-auto h-10 w-10 text-zinc-700" />
            <div className="mt-3 font-display text-2xl font-bold uppercase text-zinc-400">
              No matches yet
            </div>
            <Link
              to="/"
              data-testid="history-empty-cta"
              className="mt-5 inline-block bg-volt px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-black"
            >
              Simulate your first match →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
            {matches.map((m) => (
              <Link
                key={m.id}
                to={`/match?a=${m.team_a.code}&b=${m.team_b.code}`}
                data-testid={`history-card-${m.id}`}
                className="group block bg-black p-5 hover:bg-zinc-950"
              >
                <div className="flex items-center justify-between text-zinc-500">
                  <span className="font-mono text-[10px] uppercase tracking-widest">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                  {m.knockout && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-volt">
                      Knockout
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <Side team={m.team_a} score={m.score.a} pen={m.pens?.a} pens={m.penalties} />
                  <span className="px-2 font-display text-xs font-bold uppercase tracking-widest text-zinc-600">
                    VS
                  </span>
                  <Side team={m.team_b} score={m.score.b} pen={m.pens?.b} pens={m.penalties} right />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Side({ team, score, pen, pens, right }) {
  return (
    <div className={`flex items-center gap-2 ${right ? "flex-row-reverse text-right" : ""}`}>
      <Flag code={team.flag} className="h-5 w-7 object-cover" />
      <div className={right ? "text-right" : ""}>
        <div className="font-display text-base font-extrabold uppercase">{team.name}</div>
        <div className="font-display text-2xl font-black tabular-nums text-volt">
          {score}
          {pens && pen !== undefined && (
            <span className="ml-1 font-mono text-xs text-zinc-500">({pen})</span>
          )}
        </div>
      </div>
    </div>
  );
}
